import { type ICianMFUIElement } from '@cian/mf-registry/browser';
import { type IMicrofrontendManifest } from '@cian/mf-registry/shared';

import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type TModifier } from '@/packages/Filters';
import { type NonEmptyArray, type TJsonQuery, getUndergoundStationIdsFromJsonQuery } from '@/packages/JsonQuery';
import { type IJsonQueryUnderground } from '@/packages/api-models/common/json_query';
import { type AsyncStatus } from '@/packages/typesafe-result';

import { useReduxDispatch, useReduxSelector } from '../../../../store';
import { geoTagsActions } from '../../../../store/slices/geoTags';
import { selectUndergroundMapManifest } from '../../../../store/slices/undergroundMap/selectors/selectUndergroundMapManifest';
import { selectUndergroundMapStatus } from '../../../../store/slices/undergroundMap/selectors/selectUndergroundMapStatus';
import { loadUndergroundMapManifest } from '../../../../store/thunks/undergroundMapManifest';
import { useApplicationContext } from '../../../../utils/applicationContext';
import { MICROFRONTEND_NAME } from '../constants';
import {
  type IStationData,
  type IUndergroundMapMicrofrontendApi,
  type IUndergroundMapMicrofrontendElement,
} from '../types';

import { useLocationForUndergrounMap } from './useLocationForUndergrounMap';
import { useUndergroundMapEventsToActions } from './useUndergroundMapEventsToActions';

export const useUndergroundMapMicrofrontend = (
  ref: RefObject<ICianMFUIElement>,
  jsonQuery: TJsonQuery,
  onChange: (...modifiers: NonEmptyArray<TModifier>) => void,
): {
  getApi: () => IUndergroundMapMicrofrontendApi | undefined;
  manifest?: IMicrofrontendManifest;
  microfrontendParams: { locationId: number };
  onRefetch(): void;
  selectedStations: number[];
  status: AsyncStatus;
} => {
  const ctx = useApplicationContext();

  const [registryMicrofront, setRegistryMicrofrontend] = useState<boolean>(
    Boolean(ctx.microfrontendsRegistry.microfrontend(MICROFRONTEND_NAME)),
  );

  const locationId = useLocationForUndergrounMap();
  const manifest = useReduxSelector(selectUndergroundMapManifest);
  const status = useReduxSelector(selectUndergroundMapStatus);
  const dispatch = useReduxDispatch();
  const jsonQuerySelectedStations = useMemo(() => getUndergoundStationIdsFromJsonQuery(jsonQuery), [jsonQuery]);
  const selectedStationsRef = useRef<number[]>(jsonQuerySelectedStations);

  const getApi = useCallback(() => {
    if (!ref.current) {
      return;
    }

    return (ref.current.instance?.customElement as IUndergroundMapMicrofrontendElement)?.api;
  }, [ref]);

  const [selectedStations, setSelectedStations] = useState<number[]>(jsonQuerySelectedStations);

  /**
   * Прокидываем выбранные станции в микрофронтенд единожды
   * чтобы избежать лишних ререндеров приложения микрофронтенда,
   * изменение locationId должно вызывать ререндер микрофронетнда
   */
  const microfrontendParams = useMemo(
    () => ({ locationId, initialSelectedStations: selectedStationsRef.current }),
    [locationId],
  );

  const updateSelectedStations = useCallback(
    (stations: IStationData[]) => {
      setSelectedStations(stations.map(station => station.id));
      dispatch(geoTagsActions.addUndergroundTagsIfNotExists(stations));
      onChange({
        action: 'setUndergrounds',
        arguments: [
          ...(stations.map<IJsonQueryUnderground>(station => ({
            type: 'underground',
            id: station.id,
          })) as NonEmptyArray<IJsonQueryUnderground>),
        ],
      });
    },
    [dispatch, onChange],
  );

  const handleFetch = useCallback(() => dispatch(loadUndergroundMapManifest()), [dispatch]);

  useEffect(() => {
    if (manifest && !registryMicrofront) {
      ctx.microfrontendsRegistry.register(manifest);
      setRegistryMicrofrontend(true);
    }
  }, [ctx.microfrontendsRegistry, manifest, registryMicrofront]);

  useEffect(() => {
    if (!registryMicrofront && !manifest) {
      handleFetch();
    }
  }, [handleFetch, manifest, registryMicrofront]);

  useUndergroundMapEventsToActions(updateSelectedStations);

  return useMemo(
    () => ({
      getApi,
      manifest: registryMicrofront ? manifest : undefined,
      microfrontendParams,
      onRefetch: handleFetch,
      selectedStations,
      status,
    }),
    [getApi, registryMicrofront, manifest, microfrontendParams, handleFetch, selectedStations, status],
  );
};
